import React from "react";
import {Card, Link, Text} from "@nike/eds";
import {Home, Inbox} from "@nike/nike-design-system-icons";
import {DeclarationParty, DeclarationPartyType} from "../../graphql/graphql";

type ConsignmentInvolvedPartiesTabProps = {
    involvedParties: DeclarationParty[];
};

function renamePartyType(type: DeclarationPartyType | null | undefined): string {
    if (type === DeclarationPartyType.Importer) return "IMPORTER OF RECORD";
    // Add more conversions as needed
    if (!type) return "Unknown";
    return type as string;
}

const PartyCard = ({party}: { party: DeclarationParty }) => (
    <Card>
        <Text font="body-2" as="h4" className="eds-spacing--mb-8">
            {renamePartyType(party.type)}
        </Text>
        <div>
            <Text font="body-2" as="div" className="eds-spacing--mb-16">
                <strong>{party.name}</strong>
            </Text>
            {party.contactDetails && (
                <>
                    <div className="eds-flex eds-gap--8 eds-flex--justify-content-flex-start eds-spacing--mb-16">
                        {party.contactDetails.email && (
                            <>
                                <div>
                                    <Inbox/>
                                </div>
                                <div className="eds-flex--grow-2">
                                    <Link href={`mailto:${party.contactDetails.email}`} font="body-3">
                                        {party.contactDetails.email}
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                    {/* Uncomment if you want to show phone
          <div className="eds-flex eds-gap--8 eds-flex--justify-content-flex-start eds-spacing--mb-16">
            {party.contactDetails.phone && (
              <>
                <div>
                  <Device />
                </div>
                <div className="eds-flex--grow-2">
                  <Text font="body-3">{party.contactDetails.phone}</Text>
                </div>
              </>
            )}
          </div>
          */}
                </>
            )}
            {party.address && (
                <div className="eds-flex eds-gap--8 eds-flex--justify-content-flex-start eds-spacing--mb-8">
                    <div>
                        <Home/>
                    </div>
                    <div className="eds-flex--grow-2">
                        {party.address.addressLine1 && (
                            <Text font="body-3" as="div">
                                {party.address.addressLine1}
                            </Text>
                        )}
                        <Text font="body-3" as="div">
                            {[
                                party.address.cityName,
                                party.address.provinceStateRegionCode,
                                party.address.postalCode,
                            ]
                                .filter(Boolean)
                                .join(", ")}
                        </Text>
                        {party.address.countryCode && (
                            <Text font="body-3" as="div">
                                {party.address.countryCode}
                            </Text>
                        )}
                    </div>
                </div>
            )}
        </div>
    </Card>
);

export const ConsignmentInvolvedPartiesTab: React.FC<ConsignmentInvolvedPartiesTabProps> = ({
                                                                                                involvedParties,
                                                                                            }) => {
    // De-duplicate by id and filter out parties where all fields are null
    const dedupedParties = Array.from(
        new Map(
            (involvedParties ?? [])
                .filter((party): party is DeclarationParty => {
                    if (!party) return false;
                    // Check if party has meaningful data
                    const hasName = party.name && party.name.trim() !== "";
                    const hasEmail = party.contactDetails?.email && party.contactDetails.email.trim() !== "";
                    const hasPhone = party.contactDetails?.phone && party.contactDetails.phone.trim() !== "";
                    const hasAddress =
                        party.address &&
                        ((party.address.addressLine1 && party.address.addressLine1.trim() !== "") ||
                            (party.address.cityName && party.address.cityName.trim() !== "") ||
                            (party.address.postalCode && party.address.postalCode.trim() !== "") ||
                            (party.address.countryCode && party.address.countryCode.trim() !== "") ||
                            (party.address.provinceStateRegionCode && party.address.provinceStateRegionCode.trim() !== ""));
                    // Keep party if it has at least name or contact info or address
                    return !!hasName || !!hasEmail || !!hasPhone || !!hasAddress;
                })
                .map((party) => [party.id, party])
        ).values()
    );

    // Use the actual enum values, not string literals
    const partyTypeOrder: DeclarationPartyType[] = [
        DeclarationPartyType.Importer,
        DeclarationPartyType.Broker,
        DeclarationPartyType.Carrier,
        DeclarationPartyType.Forwarder,
    ];

    // Filter to only include types that actually exist in the data, maintaining schema order
    const orderedPartyTypes = partyTypeOrder.filter((type) =>
        dedupedParties.some((party) => party.type === type)
    );

    const numColumns = Math.min(orderedPartyTypes.length, 2);
    const edsColumnsClass = `eds-grid--cols-2 eds-grid--xl-cols-${numColumns}`;

    if (dedupedParties.length === 0) {
        return (
            <Card>
                <Text
                    font="title-4"
                    as="h4"
                    className="eds-spacing--mt-56 eds-spacing--mb-56"
                    style={{textAlign: "center"}}
                >
                    No involved parties found for this consignment.
                </Text>
            </Card>
        );
    }

    return (
        <div className={`eds-grid ${edsColumnsClass} eds-gap--16`}>
            {orderedPartyTypes.map((partyType, idx) => (
                <div key={idx}>
                    {dedupedParties
                        .filter((party) => party.type === partyType)
                        .map((party) => (
                            <PartyCard key={party.id} party={party}/>
                        ))}
                </div>
            ))}
        </div>
    );
};